import { Box, Stack, Typography } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ChevronRight from '@mui/icons-material/ChevronRight';
import RadioButtonUncheckedOutlined from '@mui/icons-material/RadioButtonUncheckedOutlined';
import { KeyValueList } from '../../components/KeyValueList';
import { LinksPanel } from '../../components/LinksPanel';
import { Panel } from '../../components/Panel';
import { useDocuments } from '../documents/useDocuments';
import { useQuotations } from '../quotations/useQuotations';
import type { Shipment } from '../shipments/sample';
import { PARTIES, type Conversation, type PartyKey } from './sample';

export function ShipmentInfoPanel({ ship }: { ship: Shipment }) {
  return (
    <Panel title="Shipment Information">
      <KeyValueList
        rows={[
          { label: 'SRB Code', value: ship.srb },
          { label: 'Customer', value: ship.customer },
          { label: 'Route', value: `${ship.from.city} (${ship.from.code}) → ${ship.to.city} (${ship.to.code})` },
          { label: 'Mode / Type', value: ship.service },
          { label: 'Status', value: ship.stage },
          { label: 'ETD', value: ship.etd },
          { label: 'ETA', value: ship.eta },
        ]}
      />
    </Panel>
  );
}

/** Where each of the three conversations stands. A click opens that conversation. */
export function StatusPanel({
  conv,
  party,
  onParty,
}: {
  conv: Conversation;
  party: PartyKey;
  onParty: (p: PartyKey) => void;
}) {
  return (
    <Panel title="Communication Status">
      <Stack spacing={0.5}>
        {PARTIES.map((p) => (
          <Stack
            key={p}
            direction="row"
            spacing={1.5}
            onClick={() => onParty(p)}
            sx={{
              alignItems: 'center',
              p: 1,
              borderRadius: 2,
              cursor: 'pointer',
              bgcolor: p === party ? 'action.hover' : undefined,
            }}
          >
            <CheckCircle color={conv.threads[p].messages.length ? 'success' : 'disabled'} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 650, fontSize: 14 }}>{p}</Typography>
              <Typography variant="caption" color="text.secondary">
                {conv.threads[p].status}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {conv.threads[p].messages.length} messages
            </Typography>
            <ChevronRight fontSize="small" color="disabled" />
          </Stack>
        ))}
      </Stack>
    </Panel>
  );
}

export function NextActionsPanel({ conv }: { conv: Conversation }) {
  return (
    <Panel title="Next Actions">
      {conv.next.length ? (
        <Stack spacing={1.25}>
          {conv.next.map((a, i) => (
            <Stack key={a.text} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
              {i === 0 ? (
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: 'warning.main', mt: 0.4 }} />
              ) : (
                <RadioButtonUncheckedOutlined sx={{ fontSize: 16, mt: 0.3 }} color="disabled" />
              )}
              <Typography variant="body2" sx={{ flex: 1 }}>
                {a.text}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {a.due || '–'}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Nothing to do next.
        </Typography>
      )}
    </Panel>
  );
}

export function RelatedItemsPanel({ conv }: { conv: Conversation }) {
  const docs = useDocuments();
  const quotes = useQuotations().items;
  return (
    <LinksPanel
      title="Related Items"
      items={[
        { label: 'Quotations', to: '/quotations', count: quotes.filter((q) => q.srb === conv.srb).length },
        { label: 'Documents', to: '/documents', count: docs.forShipment(conv.srb).length },
        { label: 'Activity History', to: '/history' },
      ]}
    />
  );
}
